import reduce from 'lodash/reduce';
import type { LocalIssueId } from '@atlassian/jira-polaris-domain-idea/src/idea/types.tsx';
import type {
	CreateIdeasBulkResponse,
	CreateIdeasRestBulkSinglePayload,
} from '@atlassian/jira-polaris-remote-issue/src/services/jira/create-idea-rest-bulk/types.tsx';
import { FIELD_TYPES } from '@atlassian/jira-polaris-domain-field/src/field-types/index.tsx';
import {
	ISSUEID_FIELDKEY,
	KEY_FIELDKEY,
} from '@atlassian/jira-polaris-domain-field/src/field/constants.tsx';
import type { IssueTypeId } from '@atlassian/jira-shared-types/src/general.tsx';
import {
	type IssueCreatedPropertyItem,
	IssueCreateStatusInTransition,
	type PropertyMaps,
	type State,
} from '../../types.tsx';
import type { FieldMapping } from '../../utils/field-mapping/types.tsx';

export function generateBulkUpdatePayload(
	issuesMap: Record<LocalIssueId, string>,
): Record<LocalIssueId, CreateIdeasRestBulkSinglePayload> {
	return Object.entries(issuesMap).reduce<Record<LocalIssueId, CreateIdeasRestBulkSinglePayload>>(
		(payload, [issueId, summary]) => {
			// eslint-disable-next-line no-param-reassign
			payload[issueId] = {
				fields: { summary },
			};
			return payload;
		},
		{},
	);
}

export function cloneStatePropertiesToUpdate(properties: PropertyMaps): PropertyMaps {
	return {
		...properties,
		string: {
			...properties.string,
			[FIELD_TYPES.SUMMARY]: {
				...properties.string[FIELD_TYPES.SUMMARY],
			},
		},
		created: {
			...properties.created,
		},
	};
}

export function updateStateProperties(
	propertiesState: PropertyMaps,
	issuesMap: Record<LocalIssueId, string>,
	createInputProperty: IssueCreatedPropertyItem,
): PropertyMaps {
	let anchorBefore = createInputProperty.anchorBefore;
	const copyOfProperties = cloneStatePropertiesToUpdate(propertiesState);
	Object.entries(issuesMap).forEach(([localIssueId, summary]) => {
		copyOfProperties.string.summary[localIssueId] = summary;
		copyOfProperties.created[localIssueId] = {
			...createInputProperty,
			anchorBefore,
			status: IssueCreateStatusInTransition,
		};
		// Move anchor to next item
		anchorBefore = localIssueId;
	});

	return copyOfProperties;
}

export const getNewSortedIds = (
	ids: LocalIssueId[],
	newIds: LocalIssueId[],
	before?: LocalIssueId,
	after?: LocalIssueId,
): LocalIssueId[] => {
	let index = ids.length;
	if (before !== undefined) {
		index = ids.indexOf(before);
	} else if (after !== undefined) {
		index = ids.indexOf(after) + 1;
	}

	return index > -1
		? [...ids.slice(0, index), ...newIds, ...ids.slice(index)]
		: [...ids, ...newIds];
};

export function getAnchorKey(
	propertiesState: PropertyMaps,
	lastId: LocalIssueId,
	ids: LocalIssueId[],
) {
	const lastIdIndex = ids.indexOf(lastId);
	const anchorAfterId = ids[lastIdIndex + 1];
	return propertiesState.string[KEY_FIELDKEY]?.[anchorAfterId];
}

export const updateLocalIssueIdToJiraId = (
	map: Record<IssueTypeId, number>,
	getState: () => Readonly<State>,
	setState: (arg1: Partial<State>) => void,
) => {
	const { properties } = getState();
	const { number } = properties;
	const issueIds = number[ISSUEID_FIELDKEY];
	setState({
		properties: {
			...properties,
			number: {
				...number,
				[ISSUEID_FIELDKEY]: {
					...issueIds,
					...map,
				},
			},
		},
	});
};

export function updatePropertiesWithResponse(
	fieldMappings: Record<string, FieldMapping<unknown>>,
	newLocalIds: LocalIssueId[],
	response: CreateIdeasBulkResponse,
	properties: PropertyMaps,
): PropertyMaps {
	return reduce(
		fieldMappings,
		(acc, mapping) => {
			let nextAcc = acc;

			newLocalIds.forEach((id) => {
				const responseIssue = response[id];
				const jiraValue = mapping.getValueFromJiraIssue(responseIssue);
				nextAcc = mapping.setImmutable(nextAcc, id, jiraValue !== null ? jiraValue : undefined);
			});

			return nextAcc;
		},
		properties,
	);
}
